import { useState } from 'react';

import type { AnsweredForm, FormAnswer, FormField } from '../fixtures/domain.js';
import { answerValues } from '../data/forms.js';
import { Icon } from '../components/Icon.js';
import { formatDate, plural } from '../util/format.js';
import { useResponseLink } from './responseLink.js';
import styles from './FormAnswersSection.module.css';

/**
 * The details panel's read of the forms answered in this conversation, newest
 * first. A row goes to the form's Responses page, open on this answer; a form
 * since deleted has no page, so its row unfolds here instead. Renders nothing
 * at all when no form has been answered; the caller hides the header too.
 */
export function FormAnswersSection({ answers }: { answers: AnsweredForm[] }) {
  const link = useResponseLink();
  const [unfolded, setUnfolded] = useState<string | null>(null);
  const ordered = [...answers].sort((a, b) =>
    b.response.submittedAt.localeCompare(a.response.submittedAt),
  );

  return (
    <ul className={styles.list}>
      {ordered.map(({ response, notice }) => {
        const opens = link?.opens(response.formId) ?? false;
        const open = unfolded === response.id;
        const content = (
          <>
            <span className={styles.disc}>
              <Icon name="listChecks" size={15} />
            </span>
            <span className={styles.headText}>
              <span className={styles.name}>{notice.name}</span>
              <span className={styles.summary}>
                {plural(response.answers.length, 'answer')} · {formatDate(response.submittedAt)}
              </span>
            </span>
            <span className={styles.chevron}>
              <Icon name={opens ? 'chevronRight' : open ? 'chevronUp' : 'chevronDown'} size={16} />
            </span>
          </>
        );
        return (
          <li key={response.id} className={open ? styles.entryOpen : styles.entry}>
            {opens ? (
              <button
                type="button"
                className={styles.head}
                onClick={() => link!.open(response.formId, response.id)}
              >
                {content}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.head}
                  aria-expanded={open}
                  onClick={() => setUnfolded(open ? null : response.id)}
                >
                  {content}
                </button>
                {open && (
                  <div className={styles.body}>
                    <FormAnswers fields={notice.fields} answers={response.answers} />
                  </div>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Question by question, answers as chips: picks, not prose — comparable at a
 * glance in a column, which is what the tap-only rule buys. Questions read
 * quieter than the answers under them, since what the reader came for is what
 * came back.
 */
export function FormAnswers({ fields, answers }: { fields: FormField[]; answers: FormAnswer[] }) {
  return (
    <dl className={styles.answers}>
      {fields.map((field) => {
        const picked = answerValues(field, answers);
        return (
          <div key={field.id} className={styles.answerEntry}>
            <dt className={styles.question}>{field.label}</dt>
            <dd className={styles.value}>
              {picked.length > 0 && field.type === 'text' ? (
                <span className={styles.text}>{picked[0]}</span>
              ) : picked.length === 0 ? (
                // Only an unanswered written question, or picks matching no
                // option, land here. Said rather than skipped.
                <span className={styles.skipped}>Not answered</span>
              ) : (
                // Keyed by position: nothing stops two options on one question
                // from carrying the same words, and the label alone collides.
                picked.map((label, at) => (
                  <span key={`${at}-${label}`} className={styles.chip}>
                    {label}
                  </span>
                ))
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
